import get from 'lodash/get';
import set from 'lodash/set';

import getMinMaxOcc, { type MinMaxOccupancy } from './get-min-max-occupancy';
import type { NormalizedFieldSet, PriceField } from './types';

/** Ported from `ProductItem.tsx`'s inline `initialValues` construction: seeds each field's path (plus
 * any `additionalNames` aliases) with its existing value or its first non-disabled option. */
function buildInitialValues(fieldSets: NormalizedFieldSet[]): Record<string, unknown> {
	const values: Record<string, unknown> = {};

	fieldSets.forEach(fieldSet => {
		fieldSet.fields.forEach(field => {
			const firstOptionValue = field.options?.find(option => !option.disabled)?.value;
			const value = field.value ?? firstOptionValue;
			set(values, field.name, value);
			field.additionalNames?.forEach(additionalName => set(values, additionalName, value));
		});
	});

	return values;
}

/** Ported from `ProductItem.tsx`: splits a price's visible fieldSets into quantity steppers
 * (adult/children counts), free-standing additional fields, and labelled/grouped fields
 * (e.g. guest details, special requests). */
function classifyFields(fieldSets: NormalizedFieldSet[]) {
	const visible = fieldSets.filter(fieldSet => !fieldSet.hidden);

	const flatFields = visible
		.filter(fieldSet => !fieldSet.label)
		.flatMap(fieldSet => fieldSet.fields);

	const quantityFields = flatFields.filter(
		field => field.name.endsWith('Quantity') || field.name.includes('Number_of')
	);
	const additionalFields = flatFields.filter(
		field => !(field.name.endsWith('Quantity') || field.name.includes('Number_of'))
	);
	const labelledFields = visible.filter(fieldSet => fieldSet.label);

	return { quantityFields, additionalFields, labelledFields };
}

export type BookingFormState = {
	initialValues: Record<string, unknown>;
	minMaxOcc: MinMaxOccupancy;
	quantityFields: PriceField[];
	additionalFields: PriceField[];
	labelledFields: NormalizedFieldSet[];
};

export default function buildBookingFormState(
	fieldSets: NormalizedFieldSet[],
	t: (text: string, params?: (string | number)[]) => string
): BookingFormState {
	const initialValues = buildInitialValues(fieldSets);
	const minMaxOcc = getMinMaxOcc({ fieldSets, t });

	const initialAdults = minMaxOcc.curAdults ?? 1;
	if ('numAdults' in initialValues) {
		initialValues['numAdults'] = initialValues['numAdults'] ?? initialAdults;
	}

	const detailsFields = get(initialValues, ['details', 'fields']) as
		| Record<string, unknown>[]
		| undefined;

	if (detailsFields) {
		const adultsField = detailsFields.find(field => 'Number_of_Adults' in field);
		if (adultsField) {
			adultsField['Number_of_Adults'] = adultsField['Number_of_Adults'] ?? initialAdults;
			minMaxOcc.curAdults = adultsField['Number_of_Adults'] as number;
		}

		const childrenField = detailsFields.find(field => 'Number_of_Children' in field);
		if (childrenField) {
			// Initial children value: minChildren if defined, else 1 if there's no adults field, else 0.
			const initialChildren = minMaxOcc.minChildren ? minMaxOcc.minChildren : adultsField ? 0 : 1;
			childrenField['Number_of_Children'] = childrenField['Number_of_Children'] ?? initialChildren;
			minMaxOcc.curChildren = childrenField['Number_of_Children'] as number;
		}
	}

	// Final pass: re-sync every `additionalNames` alias to its field's *canonical* initial value.
	// `buildInitialValues` seeds aliases from the raw `field.value`, which is often `undefined` for
	// occupancy counts (their default comes from `occupancyValidation` and is only applied to the
	// canonical path above) — so `numAdults`/`quantity.adults` would otherwise submit blank. This
	// stands in for the legacy `FieldSelector` mount-time `useEffect` that mirrored the rendered
	// value onto its aliases; `BookingForm.handleFieldChange` keeps them in sync after edits.
	fieldSets.forEach(fieldSet =>
		fieldSet.fields.forEach(field => {
			if (!field.additionalNames?.length) return;
			const canonical = get(initialValues, field.name);
			if (canonical === undefined) return;
			field.additionalNames.forEach(alias => set(initialValues, alias, canonical));
		})
	);

	const { quantityFields, additionalFields, labelledFields } = classifyFields(fieldSets);

	return { initialValues, minMaxOcc, quantityFields, additionalFields, labelledFields };
}

/** Ported from `ProductItem.tsx`'s `extraOnChange`: live-clamps the adults/children field `min`/`max`
 * against the fieldSet's overall `occupancyValidation` total as either value changes, so the pair can
 * never together exceed/underflow the group total. Mutates the fields on `minMaxOcc` in place, same as
 * the original - `FieldSelector`-equivalents read `min`/`max` straight off these objects. */
export function clampOccupancyOnChange(
	minMaxOcc: MinMaxOccupancy,
	fieldName: string,
	fieldValue: number
) {
	const isOccupancyField =
		fieldName === minMaxOcc.adultsField?.name || fieldName === minMaxOcc.childrenField?.name;

	if (!(minMaxOcc.minTotal || minMaxOcc.maxTotal) || !isOccupancyField) return;

	if (fieldName === minMaxOcc.adultsField?.name) {
		minMaxOcc.curAdults = fieldValue;
	} else if (fieldName === minMaxOcc.childrenField?.name) {
		minMaxOcc.curChildren = fieldValue;
	}

	const newTotal = minMaxOcc.curAdults + minMaxOcc.curChildren;

	if (minMaxOcc.maxTotal && newTotal >= minMaxOcc.maxTotal) {
		if (minMaxOcc.adultsField) minMaxOcc.adultsField.max = minMaxOcc.curAdults;
		if (minMaxOcc.childrenField) minMaxOcc.childrenField.max = minMaxOcc.curChildren;
	} else {
		if (minMaxOcc.adultsField) minMaxOcc.adultsField.max = minMaxOcc.maxAdults;
		if (minMaxOcc.childrenField) minMaxOcc.childrenField.max = minMaxOcc.maxChildren;
	}

	if (minMaxOcc.minTotal && newTotal <= minMaxOcc.minTotal) {
		if (minMaxOcc.adultsField) minMaxOcc.adultsField.min = minMaxOcc.curAdults;
		if (minMaxOcc.childrenField) minMaxOcc.childrenField.min = minMaxOcc.curChildren;
	} else {
		if (minMaxOcc.adultsField) minMaxOcc.adultsField.min = minMaxOcc.minAdults;
		if (minMaxOcc.childrenField) minMaxOcc.childrenField.min = minMaxOcc.minChildren;
	}
}
